import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  AddFavoriteDocument,
  ArchiveIssueDocument,
  DeleteIssueDocument,
  type EntityType,
  GetMyPreferencesDocument,
  MoveIssueToStageDocument,
  MoveIssueToWorkflowDocument,
  RemoveFavoriteDocument,
  RenameIssueDocument,
  UnarchiveIssueDocument,
  UpdateIssueAssigneesDocument,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { removeIssueFromStageCache } from "./kanban-cache-utils";
import { useAnalytics, AnalyticsContext, KanbanEvent } from "#app/lib/analytics";

/** Minimal issue shape needed by mutation handlers. */
type IssueRef = { id: string; issueId: string; name: string; stageId: string };

export function useIssueCardActions() {
  const { track } = useAnalytics();
  const [renameMutation] = useMutation(RenameIssueDocument);
  const [archiveMutation] = useMutation(ArchiveIssueDocument);
  const [unarchiveMutation] = useMutation(UnarchiveIssueDocument);
  const [deleteMutation] = useMutation(DeleteIssueDocument);
  const [moveStageMutation] = useMutation(MoveIssueToStageDocument);
  const [addFavoriteMutation] = useMutation(AddFavoriteDocument);
  const [removeFavoriteMutation] = useMutation(RemoveFavoriteDocument);
  const [moveToWorkflowMutation] = useMutation(MoveIssueToWorkflowDocument);
  const [updateAssigneesMutation] = useMutation(UpdateIssueAssigneesDocument);

  const variables = useMemo(() => ({}), []);
  const { data: prefsData } = useQuery(GetMyPreferencesDocument, {
    variables,
    fetchPolicy: "cache-first",
  });

  const favoriteIssueIds = useMemo(() => {
    const favorites = prefsData?.myPreferences?.favorites ?? [];
    return new Set(
      (favorites as { type: string; referenceId: string }[])
        .filter((f) => f.type === "issue")
        .map((f) => String(f.referenceId)),
    );
  }, [prefsData]);

  const isIssueFavorited = useCallback(
    (issueId: string) => favoriteIssueIds.has(issueId),
    [favoriteIssueIds],
  );

  const renameIssue = useCallback(
    async (issueId: string, name: string) => {
      await renameMutation({
        variables: { issueId, name },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue",
            id: issueId,
            issueId,
            name,
          },
        } as any,
      });
      track({ context: AnalyticsContext.Kanban, event: KanbanEvent.Rename });
    },
    [renameMutation, track],
  );

  const archiveIssue = useCallback(
    async (issue: IssueRef) => {
      try {
        await archiveMutation({
          variables: { issueId: issue.issueId },
          update(cache) {
            removeIssueFromStageCache(cache, issue.stageId, issue.issueId);
          },
        });
        track({ context: AnalyticsContext.Kanban, event: KanbanEvent.Archive });
        toastManager.add({ title: `"${issue.name}" archived`, type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to archive project");
      }
    },
    [archiveMutation, track],
  );

  const unarchiveIssue = useCallback(
    async (issue: IssueRef) => {
      try {
        await unarchiveMutation({
          variables: { issueId: issue.issueId },
        });
        track({ context: AnalyticsContext.Kanban, event: KanbanEvent.Unarchive });
        toastManager.add({ title: `"${issue.name}" unarchived`, type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to unarchive project");
      }
    },
    [unarchiveMutation, track],
  );

  const deleteIssue = useCallback(
    async (issue: IssueRef) => {
      try {
        await deleteMutation({
          variables: { issueId: issue.issueId },
          optimisticResponse: {
            deleteIssue: {
              __typename: "Issue",
              id: issue.id,
              issueId: issue.issueId,
              trashedAt: new Date().toISOString(),
            },
          },
          update(cache) {
            removeIssueFromStageCache(cache, issue.stageId, issue.issueId);
            // `identify` answers `undefined` for an object it cannot key, and
            // the cache reads an absent `id` as the ROOT — the eviction would
            // drop a ROOT_QUERY field instead of this issue.
            const cacheId = cache.identify({
              __typename: "Issue",
              issueId: issue.issueId,
            });
            if (cacheId) cache.evict({ id: cacheId });
            cache.gc();
          },
        });
        track({ context: AnalyticsContext.Kanban, event: KanbanEvent.Delete });
      } catch (err) {
        toastMutationError(err, "Failed to delete project");
      }
    },
    [deleteMutation, track],
  );

  const changeStage = useCallback(
    async (issue: IssueRef, toStageId: string) => {
      try {
        await moveStageMutation({
          variables: {
            issueId: issue.issueId,
            stageId: toStageId,
            sortOrder: Number.MAX_SAFE_INTEGER,
          },
          update(cache) {
            removeIssueFromStageCache(cache, issue.stageId, issue.issueId);
          },
        });
        track({ context: AnalyticsContext.Kanban, event: KanbanEvent.ChangeStage });
        toastManager.add({ title: "Stage updated", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update stage");
      }
    },
    [moveStageMutation, track],
  );

  const toggleFavorite = useCallback(
    async (issueId: string) => {
      const isFavorited = favoriteIssueIds.has(issueId);
      try {
        if (isFavorited) {
          await removeFavoriteMutation({
            variables: { type: "issue", referenceId: issueId },
          });
          toastManager.add({ title: "Removed from favorites", type: "success" });
        } else {
          await addFavoriteMutation({
            variables: { type: "issue", referenceId: issueId },
          });
          toastManager.add({ title: "Added to favorites", type: "success" });
        }
        track({ context: AnalyticsContext.Kanban, event: isFavorited ? KanbanEvent.Unfavorite : KanbanEvent.Favorite });
      } catch (err) {
        toastMutationError(err, "Failed to update favorite");
      }
    },
    [favoriteIssueIds, addFavoriteMutation, removeFavoriteMutation, track],
  );

  const moveToWorkflow = useCallback(
    async (issue: IssueRef, workflowId: string) => {
      try {
        await moveToWorkflowMutation({
          variables: { issueId: issue.issueId, workflowId },
          update(cache) {
            removeIssueFromStageCache(cache, issue.stageId, issue.issueId);
          },
        });
        track({ context: AnalyticsContext.Kanban, event: KanbanEvent.MoveWorkflow });
        toastManager.add({ title: "Moved to workflow", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to move to workflow");
      }
    },
    [moveToWorkflowMutation, track],
  );

  const updateAssignees = useCallback(
    async (issueId: string, entityIds: string[]) => {
      try {
        await updateAssigneesMutation({
          variables: {
            issueId,
            assignees: entityIds.map((id, index) => ({
              entityType: "user" as EntityType,
              entityId: id,
              isPrimary: index === 0,
            })),
          },
        });
        track({ context: AnalyticsContext.Kanban, event: KanbanEvent.UpdateAssignees, data: { count: entityIds.length } });
        toastManager.add({ title: "Assignees updated", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update assignees");
      }
    },
    [updateAssigneesMutation, track],
  );

  return {
    renameIssue,
    archiveIssue,
    unarchiveIssue,
    deleteIssue,
    changeStage,
    toggleFavorite,
    isIssueFavorited,
    moveToWorkflow,
    updateAssignees,
  };
}
